import { Container, ContainerPadding, Text, TokenTextAppearance } from '@volkswagen-onehub/components-core';
import React, { useEffect, useRef } from 'react';

import * as S from './progress-chart.styled';
import {
	extendedWarrantyEndDate,
	getMonthsBetweenDates,
	manufacturerWarrantyEndDate,
	manufacturerWarrantyTotalMonths,
} from '../../../helpers/servicesHelper';
import { Vehicle } from '@esportal/portal-service';

function useIntersectionObserver<T extends HTMLElement>(options?: IntersectionObserverInit) {
	const ref = useRef<T>(null);
	const [isIntersecting, setIsIntersecting] = React.useState(false);

	useEffect(() => {
		const element = ref.current;
		if (!element) return;

		const observer = new IntersectionObserver(([entry]) => setIsIntersecting(entry.isIntersecting), options);
		observer.observe(element);
		return () => observer.disconnect();
	}, [options]);

	return { ref, isIntersecting };
}

type ProgressChartProps = {
	vehicle: Vehicle;
	type: 'manufacturer' | 'extended';
};

const padding = {
	left: ContainerPadding.dynamic0100,
};

const ProgressChart: React.FC<ProgressChartProps> = ({ vehicle, type }) => {
	const strokeWidth = 2;
	const circleRef = useRef<SVGCircleElement>(null);

	const size = 56;
	const center = size / 2;
	const radius = Math.max(center - strokeWidth + strokeWidth / 2, strokeWidth);
	const fullCircumference = 2 * Math.PI * radius;

	// Càlcul de mesos

	let remainingMonths = 0;
	let totalMonths = 0;

	if (type === 'manufacturer') {
		const endDate = manufacturerWarrantyEndDate(
			vehicle.vgedRegistrationDate,
			vehicle.warranty?.vgedYearsWarrantyCoverage,
		);
		totalMonths = manufacturerWarrantyTotalMonths(vehicle);
		remainingMonths = endDate ? getMonthsBetweenDates(Date.now(), endDate) : 0;
	} else {
		const manufacturerEnd = manufacturerWarrantyEndDate(
			vehicle.vgedRegistrationDate,
			vehicle.warranty?.vgedYearsWarrantyCoverage,
		);
		const extensionEnd = extendedWarrantyEndDate(
			vehicle.vgedRegistrationDate,
			vehicle.warranty?.vgedYearsWarrantyCoverage,
			vehicle.warranty?.vgedYearsWarrantyExtension,
		);
		const now = Date.now();

		totalMonths = manufacturerEnd && extensionEnd ? getMonthsBetweenDates(manufacturerEnd, extensionEnd) : 0;
		remainingMonths =
			manufacturerEnd && extensionEnd && now >= manufacturerEnd
				? getMonthsBetweenDates(now, extensionEnd)
				: totalMonths;
	}

	// Càlcul de l'offset del cercle - remainingMonths en color gris
	const usedMonths = totalMonths - remainingMonths; // meses usados
	const offsetWidth = totalMonths > 0 ? (usedMonths / totalMonths) * fullCircumference : 0;

	const { ref } = useIntersectionObserver<HTMLDivElement>({
		threshold: 0.1,
	});

	useEffect(() => {
		if (circleRef.current) {
			circleRef.current.style.transition = 'stroke-dashoffset 1s ease-out';
			circleRef.current.style.strokeDasharray = `${fullCircumference}`;
			circleRef.current.style.strokeDashoffset = `${offsetWidth}`;
		}
	}, [offsetWidth, fullCircumference]);

	return (
		<S.ChartContainer ref={vehicle.warranty?.vgedYearsWarrantyExtension ? ref : undefined}>
			<S.ChartWrapper>
				<svg aria-hidden="true" preserveAspectRatio="none" viewBox={`0 0 ${size} ${size}`}>
					<S.CircleBackground cx={center} cy={center} r={radius} strokeWidth={strokeWidth} stroke="var(--color-gray)" />

					<S.Circle
						cx={center}
						cy={center}
						r={radius}
						ref={circleRef}
						strokeWidth={strokeWidth}
						stroke="#0040c5"
						transform={`rotate(-90 ${center} ${center})`}
					/>
				</svg>
			</S.ChartWrapper>

			<Container padding={padding} shrinkContent wrap="always">
				<Text appearance={TokenTextAppearance.headline200} bold>
					{remainingMonths} de {totalMonths}
				</Text>
				<Text appearance={TokenTextAppearance.label200}>Meses</Text>
			</Container>
		</S.ChartContainer>
	);
};

export default ProgressChart;
